import type { ChatUsage } from '../engine/events'

export interface ContextForecast {
  /** Share of the context window in use, clamped to 0 to 1. */
  usedFraction: number
  /** False when the context already exceeds the window, e.g. after switching to a smaller model. */
  fits: boolean
  /** Messages left before the window fills at this chat's average growth; null until two turns exist. */
  turnsLeft: number | null
}

/**
 * Measures the chat against `contextWindow`, the window of the model its next
 * message will use. Null when that window is unknown.
 */
export function forecastContext(
  { contextTokens, turnContextTokens }: Pick<ChatUsage, 'contextTokens' | 'turnContextTokens'>,
  contextWindow: number | null | undefined,
): ContextForecast | null {
  if (!contextWindow || contextWindow <= 0) {
    return null
  }

  const growth = turnContextTokens.slice(1).map((tokens, i) => tokens - turnContextTokens[i])
  // A turn that shrank the context, say after a redo, says nothing about how
  // fast the chat grows.
  const growing = growth.filter((delta) => delta > 0)
  const averageGrowth = growing.length > 0 ? growing.reduce((sum, delta) => sum + delta, 0) / growing.length : null

  return {
    usedFraction: Math.min(1, Math.max(0, contextTokens / contextWindow)),
    fits: contextTokens <= contextWindow,
    turnsLeft: averageGrowth === null ? null : Math.floor(Math.max(0, contextWindow - contextTokens) / averageGrowth),
  }
}
